import cuid from "cuid";
import {
  defaults,
  defaultsDeep,
  defaultTo,
  get,
  head,
  isEmpty,
  keyBy,
  map,
} from "lodash";
import omitDeep from "omit-deep-lodash";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { UPDATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { SUBSCRIPTION } from "utils/api/graphql/queries/subscription";
import { BANK_ACCOUNT_OWNER_TYPE, CONTACT_POINT_SYSTEM, KEYS, PAYMENT_TYPES } from "utils/constants";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

const BankInformation = ({
  project,
  onNext,
  onBack,
  updateUrl,
  currentStatus,
  tracer,
}) => {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("B2C_MILTIS.BANK_INFORMATION", { returnObjects: true });
  const [initialValues, setInitialValues] = useState(null);
  const subscriptionId =
    localStorage.getItem(KEYS.SUBSCRIPTION_ID) ||
    get(project, "fields.currentSubscription");

  const [updateSubscription] = useMutation(UPDATE_SUBSCRIPTION, {
    refetchQueries: [
      {
        query: SUBSCRIPTION,
        awaitRefetchQueries: true,
        variables: { where: { id: subscriptionId } },
      },
    ],
  });

  const initSubscription = useCallback(({ subscription: data }) => {
    const subscription = omitDeep(data, "__typename");
    const debitDays = get(
      subscription,
      "insurancePlan.fields.payment.debitDay",
      [],
    );

    const shouldBeAdherent =
      get(subscription, "payment.payer.owner.isAdherent", true) ||
      get(subscription, "insurancePlan.fields.payment.shouldBeAdherent", false);

    const isPayerReceiver = isEmpty(get(subscription, "payment.receiver.id"))
      ? true
      : get(subscription, "payment.receiver.id") ===
        get(subscription, "payment.payer.id");

    const payer = {
      id: cuid(),
      IBAN: null,
      BIC: null,
      ...subscription.payment?.payer,
      owner: {
        id: cuid(),
        ...subscription.payment?.payer?.owner,
        firstName: defaultTo(
          subscription.payment?.payer?.owner?.firstName,
          shouldBeAdherent
            ? get(subscription, "project.contact.user.firstname")
            : null,
        ),
        lastName: defaultTo(
          subscription.payment?.payer?.owner?.lastName,
          shouldBeAdherent
            ? get(subscription, "project.contact.user.lastname")
            : null,
        ),
        gender: defaultTo(
          subscription.payment?.payer?.owner?.gender,
          shouldBeAdherent
            ? get(subscription, "project.contact.user.gender")
            : null,
        ),
        isAdherent: defaultTo(
          subscription.payment?.payer?.owner?.isAdherent,
          shouldBeAdherent,
        ),
        type: get(subscription, "insurancePlan.fields.tns")
          ? BANK_ACCOUNT_OWNER_TYPE.COMPANY
          : BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
      },
    };

    const receiver = isPayerReceiver
      ? { ...payer, owner: { ...payer.owner } }
      : defaultsDeep(subscription.payment?.receiver, {
          id: cuid(),
          IBAN: null,
          BIC: null,
          owner: {
            id: cuid(),
            isAdherent: false,
            gender: null,
          },
        });

    const initialValues = {
      isPayerReceiver,
      telecoms: {
        receiver: defaults(
          keyBy(
            get(subscription, "payment.receiver.owner.telecoms", []),
            "system",
          ),
          {
            [CONTACT_POINT_SYSTEM.ADDRESS]: { value: null },
            [CONTACT_POINT_SYSTEM.ZIP_CODE]: { value: null },
            [CONTACT_POINT_SYSTEM.CITY]: { value: null },
          },
        ),
      },
      payment: {
        id: cuid(),
        type:
          head(get(subscription, "insurancePlan.fields.payment.type")) ||
          PAYMENT_TYPES.DEBIT,
        contact: get(subscription, "project.contact"),
        ...subscription.payment,
        debitDay: get(
          subscription,
          "payment.debitDay",
          debitDays.length === 1 ? head(debitDays) : null,
        ),
        payer,
        receiver,
      },
    };

    setInitialValues(initialValues);
  }, []);

  const { data: subscription, loading } = useQuery(SUBSCRIPTION, {
    variables: { where: { id: subscriptionId } },
    onCompleted: initSubscription,
  });

  const data = omitDeep(subscription, "__typename");

  // Check if names match adherent names (case insensitive)
  const checkIfNamesMatchAdherent = (firstName, lastName, gender) => {
    const adherentFirstName = get(project, "contact.user.firstname", "").trim();
    const adherentLastName = get(project, "contact.user.lastname", "").trim();

    const ownerFirstName = (firstName || "").trim();
    const ownerLastName = (lastName || "").trim();

    const firstNameMatch =
      ownerFirstName.toLowerCase() === adherentFirstName.toLowerCase();
    const lastNameMatch =
      ownerLastName.toLowerCase() === adherentLastName.toLowerCase();
    const genderMatch = gender === get(project, "contact.user.gender", "");

    return firstNameMatch && lastNameMatch && (gender ? genderMatch : true);
  };

  const onSubmit = (values) => {
    // Update isAdherent for payer based on name comparison
    const payerFirstName = get(values, "payment.payer.owner.firstName", "");
    const payerLastName = get(values, "payment.payer.owner.lastName", "");
    const payerGender = get(values, "payment.payer.owner.gender", "");
    const telecoms = get(values, "telecoms.receiver", []);
    const updatedTelecoms = map(telecoms, (item, key) =>
      defaults(
        { ...item, value: item?.value != null ? String(item.value) : null },
        { id: cuid(), system: key },
      ),
    );

    if (payerFirstName && payerLastName) {
      const payerIsAdherent = checkIfNamesMatchAdherent(
        payerFirstName,
        payerLastName,
        payerGender,
      );
      values.payment.payer.owner.isAdherent = payerIsAdherent;
    }

    // Update isAdherent for receiver based on name comparison (only if receiver is different from payer)
    if (!values.isPayerReceiver) {
      const receiverFirstName = get(
        values,
        "payment.receiver.owner.firstName",
        "",
      );
      const receiverLastName = get(
        values,
        "payment.receiver.owner.lastName",
        "",
      );
      values.payment.receiver.owner.telecoms = updatedTelecoms;
      if (receiverFirstName && receiverLastName) {
        const receiverIsAdherent = checkIfNamesMatchAdherent(
          receiverFirstName,
          receiverLastName,
        );
        values.payment.receiver.owner.isAdherent = receiverIsAdherent;
      }
    } else {
      // If receiver is same as payer, use payer's isAdherent value
      values.payment.receiver.owner.isAdherent = get(
        values,
        "payment.payer.owner.isAdherent",
        false,
      );
    }

    const payment = omitDeep(values.payment, ["__typename", "contact"]);

    return new Promise((resolve, reject) => {
      updateSubscription({
        variables: {
          where: {
            id: subscriptionId,
          },
          data: { payment },
        },
        onCompleted: () => {
          const source = localStorage.getItem(KEYS.SOURCE);
          updateUrl({
            subscriptionId,
            url: get(
              data,
              "subscription.insurancePlan.config.generatedUrl",
              `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
            ),
          });

          Promise.resolve(onNext({ payload: {} })).then(resolve).catch(reject);
        },
        onError: reject,
      });
    });
  };

  if (loading || isEmpty(initialValues)) return <Loader />;

  return (
    <>
      <Navbar
        project={project}
        progress={currentStatus?.progress}
        tracer={tracer}
        showCart
        showProfile
        step={4}
        stepCount={5}
        title="Mes informations contractuelles"
      />
      <View
        subscription={omitDeep(get(data, "subscription", {}), "__typename")}
        project={project}
        onNext={onSubmit}
        onBack={onBack}
        initialValues={initialValues}
        translate={TRANSLATION}
      />
    </>
  );
};

export default BankInformation;
